/**
 * Sprint 5B — Pause dialog: hold / betalingsregeling / manueel.
 * Reden altijd verplicht. Optioneel: notitie, startdatum, einddatum.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PauseCircle } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const REASON_LABELS = {
  hold: "Op hold — algemene reden voor stopzetten",
  payment_plan: "Betalingsregeling — klant heeft afspraak",
  manual: "Manueel gepauzeerd",
};

export default function ReminderPauseDialog({
  open, onOpenChange, invoiceId, defaultReason = "hold", onPaused,
}) {
  const [reason, setReason] = useState(defaultReason);
  const [note, setNote] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(defaultReason);
      setNote("");
      setStartDate("");
      setEndDate("");
      setSaving(false);
    }
  }, [open, defaultReason]);

  const isPaymentPlan = reason === "payment_plan";

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error("Reden/notitie is verplicht");
      return;
    }
    setSaving(true);
    try {
      // Bouw note met eventuele start/end datum meta
      const fullNote = isPaymentPlan
        ? [
            note.trim(),
            startDate ? `Startdatum: ${startDate}` : null,
            endDate ? `Einddatum: ${endDate}` : null,
            "Let op: einddatum activeert reminders NIET automatisch — medewerker moet handmatig hervatten.",
          ].filter(Boolean).join(" — ")
        : note.trim();

      const r = await adminApi.post(
        `/admin/sales-invoices/${invoiceId}/reminder-pause`,
        { reason, note: fullNote },
      );
      const followup = r.data?.followup_status;
      const labelMap = {
        hold: "op hold gezet",
        betalingsregeling: "in betalingsregeling gezet",
        gepauzeerd: "gepauzeerd",
      };
      toast.success(`Opvolging ${labelMap[followup] || followup}`);
      onOpenChange(false);
      if (typeof onPaused === "function") onPaused(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-none"
        data-testid="reminder-pause-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <PauseCircle className="w-5 h-5" />
            Opvolging pauzeren
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Reden</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="reminder-pause-reason"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(REASON_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">
              Reden/notitie (verplicht — komt in audit-trail)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-none mt-1"
              placeholder="Bijvoorbeeld: klant meldt tijdelijke betalingsproblemen, opvolging tot 15-04..."
              rows={3}
              data-testid="reminder-pause-note"
            />
          </div>

          {isPaymentPlan && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Startdatum regeling (optioneel)</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-none mt-1"
                  data-testid="reminder-pause-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">Einddatum regeling (optioneel)</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-none mt-1"
                  data-testid="reminder-pause-end-date"
                />
              </div>
            </div>
          )}

          {isPaymentPlan && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-2 text-[11px] text-amber-900">
              Bij einddatum worden reminders <strong>niet automatisch</strong>
              hervat. Klik &quot;Hervatten&quot; wanneer u handmatig wilt
              herstarten.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            disabled={saving}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !note.trim()}
            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
            data-testid="reminder-pause-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
            Pauzeren bevestigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
